'use client';

import { useState, useRef, useEffect } from 'react';
import {
  CONTACT_SUBJECTS,
  SMS_CONSENT_TEXT,
  contactContract,
  contactField,
} from '@/lib/contact-contract';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  smsConsent: boolean;
}

const EMPTY_FORM: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  smsConsent: false,
};

const fields = {
  firstName: contactField('firstName'),
  lastName: contactField('lastName'),
  email: contactField('email'),
  phone: contactField('phone'),
  message: contactField('message'),
};

// ---------------------------------------------------------------------------
// Cloudflare Turnstile

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      theme?: 'light' | 'dark' | 'auto';
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '';

let turnstileLoader: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoader) {
    turnstileLoader = new Promise<TurnstileApi>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onload = () =>
        window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile unavailable'));
      script.onerror = () => reject(new Error('Turnstile failed to load'));
      document.head.appendChild(script);
    }).catch((error) => {
      turnstileLoader = null;
      throw error;
    });
  }
  return turnstileLoader;
}

function phoneDigits(value: string) {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits;
}

const inputClass =
  'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-sage-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isSubjectDropdownOpen, setIsSubjectDropdownOpen] = useState(false);
  const [activeOption, setActiveOption] = useState(0);
  const [token, setToken] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const subjectButtonRef = useRef<HTMLButtonElement | null>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const widgetHost = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  // One ID per filled-in form, reused on retries so the hub never stores a duplicate.
  const submissionId = useRef<string | null>(null);
  // When the form was shown; the hub treats instant submissions as likely bots.
  const startedAt = useRef<number | null>(null);
  const success = submitStatus === 'success';

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsSubjectDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isSubjectDropdownOpen) optionRefs.current[activeOption]?.focus();
  }, [isSubjectDropdownOpen, activeOption]);

  useEffect(() => {
    if (!success) startedAt.current ??= Date.now();
  }, [success]);

  useEffect(() => {
    if (success || !TURNSTILE_SITE_KEY) return;
    const host = widgetHost.current;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !host) return;
        widgetId.current = turnstile.render(host, {
          sitekey: TURNSTILE_SITE_KEY,
          action: 'contact',
          theme: 'light',
          callback: (value) => {
            setToken(value);
            setCaptchaError(false);
          },
          'expired-callback': () => setToken(''),
          'error-callback': () => {
            setToken('');
            setCaptchaError(true);
          },
        });
      })
      .catch(() => {
        if (!cancelled) setCaptchaError(true);
      });
    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = null;
      setToken('');
    };
  }, [success]);

  function resetWidget() {
    setToken('');
    if (widgetId.current) window.turnstile?.reset(widgetId.current);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') setPhoneError('');
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  function chooseSubject(index: number) {
    setFormData((prev) => ({ ...prev, subject: CONTACT_SUBJECTS[index] }));
    setIsSubjectDropdownOpen(false);
    subjectButtonRef.current?.focus();
  }

  function openSubjectDropdown() {
    const current = CONTACT_SUBJECTS.indexOf(formData.subject as (typeof CONTACT_SUBJECTS)[number]);
    setActiveOption(current >= 0 ? current : 0);
    setIsSubjectDropdownOpen(true);
  }

  function handleSubjectButtonKey(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openSubjectDropdown();
    }
  }

  function handleOptionKey(e: React.KeyboardEvent<HTMLLIElement>, index: number) {
    const last = CONTACT_SUBJECTS.length - 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveOption(index === last ? 0 : index + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveOption(index === 0 ? last : index - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveOption(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveOption(last);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      chooseSubject(index);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsSubjectDropdownOpen(false);
      subjectButtonRef.current?.focus();
    } else if (e.key === 'Tab') {
      setIsSubjectDropdownOpen(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setSubmitStatus('idle');
    setErrorMessage('');

    const phone = formData.phone.trim();
    if (phone && phoneDigits(phone).length !== 10) {
      setPhoneError('Please enter a 10-digit US phone number, or leave it blank.');
      phoneRef.current?.focus();
      return;
    }
    if (formData.smsConsent && !phone) {
      setPhoneError('Add a phone number so we can text you, or uncheck the text box below.');
      phoneRef.current?.focus();
      return;
    }
    if (!TURNSTILE_SITE_KEY) {
      setSubmitStatus('error');
      setErrorMessage('Our contact form isn’t available right now. Please call or email us instead.');
      return;
    }
    if (!token) {
      setSubmitStatus('error');
      setErrorMessage('Please complete the verification and try again.');
      return;
    }

    const form = new window.FormData(e.currentTarget);
    submissionId.current ??= crypto.randomUUID();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submissionId: submissionId.current,
          turnstileToken: token,
          // The hidden "company" field is the honeypot; the hub holds anything that fills it.
          honeypot: String(form.get('company') ?? ''),
          startedAt: startedAt.current ?? undefined,
          pageUrl: window.location.href,
          referrer: document.referrer || undefined,
          data: {
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim(),
            phone,
            subject: formData.subject,
            message: formData.message.trim(),
            smsConsent: formData.smsConsent && Boolean(phone),
          },
        }),
      });

      const result = (await response.json().catch(() => null)) as
        | { ok?: boolean; message?: string; error?: string }
        | null;

      if (response.ok && result?.ok) {
        submissionId.current = null;
        startedAt.current = null;
        setSuccessMessage(result.message || contactContract.successMessage);
        setSubmitStatus('success');
        setFormData(EMPTY_FORM);
        return;
      }
      setSubmitStatus('error');
      setErrorMessage(result?.error || 'Something went wrong. Please try again.');
    } catch {
      setSubmitStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
    resetWidget();
  };

  if (success) {
    return (
      <div className="text-center py-12" role="status" aria-live="polite">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-3">Message Sent Successfully!</h3>
        <p className="text-gray-600 mb-6">{successMessage}</p>
        <button
          type="button"
          onClick={() => setSubmitStatus('idle')}
          className="inline-flex items-center gap-2 bg-sage-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-sage-700 transition-colors"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" aria-busy={isSubmitting}>
      <div role="alert" aria-live="assertive">
        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800 font-medium">Error sending message</p>
            </div>
            <p className="text-red-700 mt-1">{errorMessage}</p>
          </div>
        )}
      </div>

      {/* Honeypot: hidden from people and screen readers; bots tend to fill it in. */}
      <div className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor="contact-company">Company</label>
        <input id="contact-company" name="company" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            maxLength={fields.firstName.maxLength}
            autoComplete="given-name"
            disabled={isSubmitting}
            className={`${inputClass} border-gray-300`}
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            maxLength={fields.lastName.maxLength}
            autoComplete="family-name"
            disabled={isSubmitting}
            className={`${inputClass} border-gray-300`}
          />
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          maxLength={fields.email.maxLength}
          autoComplete="email"
          disabled={isSubmitting}
          className={`${inputClass} border-gray-300`}
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number (Optional)
        </label>
        <input
          ref={phoneRef}
          type="tel"
          id="phone"
          name="phone"
          inputMode="tel"
          value={formData.phone}
          onChange={handleChange}
          maxLength={fields.phone.maxLength}
          autoComplete="tel"
          disabled={isSubmitting}
          aria-invalid={phoneError ? true : undefined}
          aria-describedby={phoneError ? 'phone-error' : undefined}
          className={`${inputClass} ${phoneError ? 'border-red-400' : 'border-gray-300'}`}
        />
        {phoneError && (
          <p id="phone-error" className="text-sm text-red-700 mt-2">
            {phoneError}
          </p>
        )}
      </div>

      <div>
        <span id="subject-label" className="block text-sm font-medium text-gray-700 mb-2">
          Subject
        </span>
        <div className="relative" ref={dropdownRef}>
          <button
            ref={subjectButtonRef}
            type="button"
            id="subject"
            onClick={() =>
              !isSubmitting && (isSubjectDropdownOpen ? setIsSubjectDropdownOpen(false) : openSubjectDropdown())
            }
            onKeyDown={handleSubjectButtonKey}
            disabled={isSubmitting}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 hover:border-sage-500 focus:ring-2 focus:ring-sage-500 focus:border-sage-500 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
            aria-haspopup="listbox"
            aria-expanded={isSubjectDropdownOpen}
            aria-controls="subject-options"
            aria-labelledby="subject-label subject"
          >
            <span className={formData.subject ? 'text-gray-900' : 'text-gray-500'}>
              {formData.subject || 'Select a subject'}
            </span>
            <svg
              className={`w-4 h-4 transition-transform ${isSubjectDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isSubjectDropdownOpen && (
            <ul
              id="subject-options"
              role="listbox"
              aria-labelledby="subject-label"
              className="absolute top-full mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10 overflow-hidden max-h-60 overflow-y-auto animate-dropdown"
            >
              {CONTACT_SUBJECTS.map((option, index) => (
                <li
                  key={option}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  role="option"
                  aria-selected={formData.subject === option}
                  tabIndex={index === activeOption ? 0 : -1}
                  onClick={() => chooseSubject(index)}
                  onKeyDown={(e) => handleOptionKey(e, index)}
                  className={`w-full px-4 py-2 text-left cursor-pointer hover:bg-sage-50 focus:bg-sage-50 transition-colors ${
                    formData.subject === option ? 'bg-sage-100 text-sage-900 font-medium' : 'text-gray-700'
                  }`}
                >
                  {option}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          Message
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          maxLength={fields.message.maxLength}
          rows={6}
          disabled={isSubmitting}
          className={`${inputClass} border-gray-300 resize-vertical`}
          placeholder="How can we help you?"
        ></textarea>
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="smsConsent"
          name="smsConsent"
          checked={formData.smsConsent}
          onChange={(e) => {
            const checked = e.target.checked;
            if (!checked) setPhoneError('');
            setFormData((prev) => ({ ...prev, smsConsent: checked }));
          }}
          disabled={isSubmitting}
          className="mt-1 h-5 w-5 shrink-0 rounded border-gray-300 accent-sage-600 disabled:opacity-50"
        />
        <label htmlFor="smsConsent" className="text-sm text-gray-600">
          {SMS_CONSENT_TEXT}
        </label>
      </div>

      <div>
        <div ref={widgetHost} />
        {(captchaError || !TURNSTILE_SITE_KEY) && (
          <p className="text-sm text-gray-600 mt-2">
            Verification couldn’t load. Refresh the page or try another browser.
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-sage-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-sage-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Sending Message...
          </span>
        ) : (
          'Send Message'
        )}
      </button>
    </form>
  );
}
